import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

const optionalString = (max: number) =>
  z
    .string()
    .nullish()
    .transform(value => value?.trim().slice(0, max) || null);

export const parseBeaconBody = (body: unknown) => {
  if (typeof body !== 'string') return body;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

const LinkInBioEventBodySchema = z
  .object({
    seller: ObjectIdStringValidationSchema,
    type: z.enum(['view', 'click']),
    viewId: z.string().regex(/^[a-zA-Z0-9-]{8,64}$/),
    item: optionalString(120),
    label: optionalString(100),
    referrer: optionalString(2048),
    utmSource: optionalString(100),
  })
  .meta({ id: 'LinkInBioEventRequest' });

// sendBeacon posts text/plain to avoid a CORS preflight, which koa-body hands over as a string.
export const LinkInBioEventRequestValidationSchema = z.preprocess(
  parseBeaconBody,
  LinkInBioEventBodySchema,
);

export const LINK_IN_BIO_ANALYTICS_UNITS = ['hour', 'day', 'week', 'month', 'year'] as const;

export const GetLinkInBioAnalyticsQueryValidationSchema = z.object({
  // Left out for All time.
  dateStart: z.coerce.date().optional(),
  dateEnd: z.coerce.date(),
  unit: z.enum(LINK_IN_BIO_ANALYTICS_UNITS).default('day'),
  timezoneOffset: z
    .string()
    .regex(/^[+-]\d{2}:\d{2}$/)
    .default('+00:00'),
});

const count = z.number().int().nonnegative();

export const LinkInBioAnalyticsResponseValidationSchema = z
  .object({
    start: z.date(),
    unit: z.enum(LINK_IN_BIO_ANALYTICS_UNITS),
    totals: z.object({
      views: count,
      visitors: count,
      clicks: count,
      viewsWithClicks: count,
      signups: count,
    }),
    series: z.array(
      z.object({ date: z.string(), views: count, visitors: count, clicks: count, signups: count }),
    ),
    items: z.array(z.object({ item: z.string(), label: z.string().nullable(), clicks: count })),
    sources: z.array(z.object({ source: z.string(), views: count })),
    devices: z.array(z.object({ device: z.enum(['mobile', 'tablet', 'desktop']), views: count })),
  })
  .meta({ id: 'LinkInBioAnalytics' });

export type LinkInBioEventRequest = z.infer<typeof LinkInBioEventBodySchema>;
export type LinkInBioAnalyticsResponse = z.infer<typeof LinkInBioAnalyticsResponseValidationSchema>;
